import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';

import {
  Loader2,
  Download,
  RefreshCw,
  ArrowLeft,
  AlertCircle,
} from 'lucide-react';

import Toolbar from '@/components/Toolbar';

import { Button } from '../../components/ui/button';
import { apiService } from '../../lib/axios';

const POLL_INTERVAL = 2000; // 2 seconds
const POLL_TIMEOUT = 60000; // 60 seconds

const PDFLoadingPanel = () => {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <p className="text-lg text-gray-600">Generating PDF...</p>
        <p className="text-sm text-gray-500 mt-2">
          This may take a few seconds
        </p>
      </div>
    </div>
  );
};

const PDFErrorPanel = ({ error, fetchPDF }) => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center mb-4">
          <AlertCircle className="h-12 w-12 text-red-500 mr-4" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Error Loading PDF
            </h2>
            <p className="text-gray-600 mt-1">{error}</p>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={fetchPDF} variant="outline" className="flex-1">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
          <Button onClick={() => navigate(-1)} className="flex-1">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function DocumentPDFViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();

    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await apiService.documents.getPDFUrl(id);
        if (response.data.success && response.data.data.pdfUrl) {
          setPdfUrl(response.data.data.pdfUrl);
          setRegenerating(false);
          setLoading(false);
          stopPolling();
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, POLL_INTERVAL);

    // Set timeout to stop polling after 60 seconds
    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      setError('PDF generation timed out. Please try again.');
      setRegenerating(false);
      setLoading(false);
    }, POLL_TIMEOUT);
  }, [id, stopPolling]);

  const fetchPDF = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.documents.getPDFUrl(id);

      if (response.data.success) {
        if (response.data.data.pdfUrl) {
          setPdfUrl(response.data.data.pdfUrl);
          setLoading(false);
        } else {
          // PDF not ready yet, start polling
          startPolling();
        }
      } else {
        setError(response.data.error || 'Failed to load PDF');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching PDF:', err);
      setError(
        err.response?.data?.error ||
          'Failed to load PDF. The PDF may not have been generated yet.'
      );
      setLoading(false);
    }
  }, [id, startPolling]);

  useEffect(() => {
    fetchPDF();
  }, [fetchPDF]);

  const handleRegeneratePDF = async () => {
    try {
      setRegenerating(true);
      setError(null);
      setPdfUrl(null); // Clear current PDF

      // Trigger regeneration (async - returns immediately)
      await apiService.documents.regeneratePDF(id);

      // Start polling for completion
      startPolling();
    } catch (err) {
      console.error('Error regenerating PDF:', err);
      setError(err.response?.data?.error || 'Failed to regenerate PDF');
      setRegenerating(false);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `document-${id}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // if (loading) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <div className="text-center">
  //         <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
  //         <p className="text-lg text-gray-600">Generating PDF...</p>
  //         <p className="text-sm text-gray-500 mt-2">
  //           This may take a few seconds
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  // if (error) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen p-4">
  //       <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
  //         <div className="flex items-center mb-4">
  //           <AlertCircle className="h-12 w-12 text-red-500 mr-4" />
  //           <div>
  //             <h2 className="text-xl font-semibold text-gray-900">
  //               Error Loading PDF
  //             </h2>
  //             <p className="text-gray-600 mt-1">{error}</p>
  //           </div>
  //         </div>
  //         <div className="flex gap-3 mt-6">
  //           <Button onClick={fetchPDF} variant="outline" className="flex-1">
  //             <RefreshCw className="h-4 w-4 mr-2" />
  //             Try Again
  //           </Button>
  //           <Button onClick={() => navigate('/documents')} className="flex-1">
  //             <ArrowLeft className="h-4 w-4 mr-2" />
  //             Back to Documents
  //           </Button>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="pt-14 bg-gray-50">
      {/* Header Toolbar */}
      <Toolbar>
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate(-1)} variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          {false && (
            <Button
              onClick={() => navigate(`/document/${id}`)}
              variant="outline"
              size="sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Edit
            </Button>
          )}
          <h1 className="text-lg font-semibold text-gray-900">
            Document PDF Viewer
          </h1>
        </div>

        {!(loading || error) && (
          <div className="flex items-center gap-2">
            <Button
              onClick={handleRegeneratePDF}
              variant="outline"
              size="sm"
              disabled={regenerating}
            >
              {regenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate PDF
                </>
              )}
            </Button>

            <Button onClick={handleDownload} size="sm">
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        )}
      </Toolbar>

      {loading ? (
        <PDFLoadingPanel />
      ) : error ? (
        <PDFErrorPanel {...{ error, fetchPDF }} />
      ) : (
        <div className="flex-1 overflow-hidden">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title="Document PDF"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">No PDF available</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
